import React, { useEffect, useRef } from 'react';
import Navbar from './home/components/Navbar';
import Footer from './home/components/Footer';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

export default function Pricing() {
  const headerRef = useRef<HTMLDivElement>(null);
  const b2bCardRef = useRef<HTMLDivElement>(null);
  const tapnCardRef = useRef<HTMLDivElement>(null);
  const integrationsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let ctx = gsap.context(() => {
      // Header Animation
      gsap.fromTo(headerRef.current,
        { opacity: 0, y: 40 },
        { opacity: 1, y: 0, duration: 1, ease: 'power3.out' }
      );

      // B2B Card Animation
      gsap.fromTo(b2bCardRef.current,
        { opacity: 0, y: 80 },
        {
          opacity: 1,
          y: 0,
          duration: 1,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: b2bCardRef.current,
            start: 'top 85%',
          }
        }
      );

      // Tapn Card Animation
      gsap.fromTo(tapnCardRef.current,
        { opacity: 0, y: 80 },
        {
          opacity: 1,
          y: 0,
          duration: 1,
          delay: 0.2, // Stagger effect
          ease: 'power3.out',
          scrollTrigger: {
            trigger: tapnCardRef.current,
            start: 'top 85%',
          }
        }
      );

      // Integrations Animation
      gsap.fromTo(integrationsRef.current,
        { opacity: 0, y: 60, scale: 0.98 },
        {
          opacity: 1,
          y: 0,
          scale: 1,
          duration: 1.2,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: integrationsRef.current,
            start: 'top 85%',
          }
        }
      );
    });

    return () => ctx.revert();
  }, []);

  return (
    <div className="min-h-screen bg-[#fafafa] font-sans selection:bg-blue-100 overflow-x-hidden">
      <Navbar />
      
      <main className="pt-32 pb-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          
          {/* Header */}
          <div ref={headerRef} className="text-center max-w-4xl mx-auto mb-20 opacity-0">
            <h1 className="text-5xl md:text-6xl font-extrabold text-gray-900 mb-6 tracking-tight leading-tight">
              Powerful Infrastructure. <br className="hidden md:block"/>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">
                Transparent Pricing.
              </span>
            </h1>
            <p className="text-xl text-gray-500 font-medium">
              We leverage wholesale rates to bring you enterprise-grade payment orchestration at a fraction of the cost of heavyweights like Stripe and Square.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
            
            {/* Paygent B2B Anchor */}
            <div ref={b2bCardRef} className="opacity-0 bg-white rounded-[2.5rem] border border-gray-100 p-10 hover:shadow-2xl transition-all duration-500 overflow-hidden relative group">
              <div className="absolute top-0 right-0 w-[400px] h-[400px] opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none">
                <div className="absolute inset-0 bg-blue-50 rounded-full blur-[100px] transform translate-x-1/2 -translate-y-1/2"></div>
              </div>
              
              <div className="relative z-10 flex flex-col h-full">
                <div className="flex justify-between items-start mb-8">
                  <div>
                    <span className="inline-block px-4 py-1.5 rounded-full bg-blue-50 text-blue-700 font-bold text-sm mb-4">
                      PAYGENT PLATFORM
                    </span>
                    <h2 className="text-4xl font-extrabold text-gray-900">Developer & B2B</h2>
                    <p className="text-gray-500 mt-2 font-medium">For SaaS, Wholesalers, and Platforms.</p>
                  </div>
                </div>

                <div className="mb-10">
                  <img src="/pricing/pricing_b2b_art_1776903240404.png" alt="B2B Architecture" className="w-full h-64 object-cover rounded-2xl shadow-sm border border-gray-50" />
                </div>

                <div className="grid grid-cols-2 gap-4 mb-8">
                  <div className="bg-gray-50 rounded-2xl p-6 border border-gray-100">
                    <p className="text-gray-500 text-sm font-semibold mb-1 uppercase tracking-wide">Monthly Gateway</p>
                    <p className="text-3xl font-extrabold text-gray-900">$15</p>
                  </div>
                  <div className="bg-gray-50 rounded-2xl p-6 border border-gray-100">
                    <p className="text-gray-500 text-sm font-semibold mb-1 uppercase tracking-wide">Per Transaction</p>
                    <p className="text-3xl font-extrabold text-gray-900">$0.10</p>
                  </div>
                </div>

                <div className="bg-green-50 text-green-800 p-4 rounded-xl border border-green-100 flex items-center gap-3 mb-8">
                  <i className="ri-shield-check-fill text-xl"></i>
                  <p className="font-semibold text-sm">40% cheaper than Authorize.net ($25/mo)</p>
                </div>

                <div className="space-y-4 mt-auto">
                  <h3 className="font-bold text-gray-900">Essential Add-ons:</h3>
                  <div className="flex justify-between border-b border-gray-100 pb-3">
                    <span className="text-gray-600 font-medium">Level III Advantage</span>
                    <span className="font-bold text-gray-900">$45/mo</span>
                  </div>
                  <div className="flex justify-between border-b border-gray-100 pb-3">
                    <span className="text-gray-600 font-medium">ACH Payments</span>
                    <span className="font-bold text-gray-900">$25/mo</span>
                  </div>
                  <div className="flex justify-between pb-3">
                    <span className="text-gray-600 font-medium">Customer Vault (Tokenization)</span>
                    <span className="font-bold text-blue-600">Included</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Tapn Retail */}
            <div ref={tapnCardRef} className="opacity-0 rounded-[2.5rem] border border-green-200 p-10 hover:shadow-[0_20px_50px_rgba(34,197,94,0.15)] transition-all duration-500 overflow-hidden relative group bg-gradient-to-br from-[#f0fdf4] to-[#dcfce7]">
              {/* Turf Pattern Overlay */}
              <div className="absolute inset-0 opacity-20 pointer-events-none" style={{ backgroundImage: 'repeating-linear-gradient(90deg, transparent, transparent 40px, rgba(34, 197, 94, 0.1) 40px, rgba(34, 197, 94, 0.1) 80px)' }}></div>
              
              <div className="absolute top-0 left-0 w-[400px] h-[400px] opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none">
                <div className="absolute inset-0 bg-green-400 rounded-full blur-[120px] transform -translate-x-1/2 -translate-y-1/2 opacity-30"></div>
              </div>
              
              <div className="relative z-10 flex flex-col h-full">
                <div className="flex justify-between items-start mb-8">
                  <div>
                    <span className="inline-block px-4 py-1.5 rounded-full bg-green-600 text-white font-bold text-sm mb-4 shadow-sm border border-green-500">
                      TAPN PRODUCT
                    </span>
                    <img src="/tapn-logo.png" alt="Tapn Logo" className="h-36 w-auto mb-4 drop-shadow-md -ml-2" onError={(e) => { e.currentTarget.style.display = 'none' }} />
                    <h2 className="text-4xl font-extrabold text-green-950">Retail & World Cup</h2>
                    <p className="text-green-800 mt-2 font-medium">For Bars, Restaurants, and Festivals.</p>
                  </div>
                </div>

                <div className="mb-10 relative">
                  <div className="absolute inset-0 bg-green-500 rounded-2xl transform translate-x-2 translate-y-2 opacity-20"></div>
                  <img src="/pricing/pricing_tapn_soccer_art_1776903959889.png" alt="Tapn SoftPOS Soccer Theme" className="w-full h-64 object-cover rounded-2xl shadow-lg border-2 border-white relative z-10" />
                </div>

                <div className="grid grid-cols-2 gap-4 mb-8">
                  <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-green-100 shadow-sm">
                    <p className="text-green-700 text-sm font-semibold mb-1 uppercase tracking-wide">Monthly Fee</p>
                    <p className="text-3xl font-extrabold text-green-950">$0</p>
                  </div>
                  <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-green-100 shadow-sm">
                    <p className="text-green-700 text-sm font-semibold mb-1 uppercase tracking-wide">Per "Tap" Fee</p>
                    <p className="text-3xl font-extrabold text-green-950">$0.20</p>
                  </div>
                </div>

                <div className="bg-white text-green-800 p-4 rounded-xl border border-green-200 shadow-sm flex items-center gap-3 mb-8 relative overflow-hidden">
                  <div className="absolute left-0 top-0 bottom-0 w-1 bg-green-500"></div>
                  <i className="ri-flashlight-fill text-xl text-green-600"></i>
                  <p className="font-semibold text-sm">Beats Square & Toast on small ticket markups</p>
                </div>

                <div className="space-y-4 mt-auto bg-white/60 p-6 rounded-2xl border border-green-100">
                  <h3 className="font-bold text-green-950">Essential Add-ons:</h3>
                  <div className="flex justify-between border-b border-green-200/50 pb-3">
                    <span className="text-green-800 font-medium">Mobile App (iProcess)</span>
                    <span className="font-bold text-green-950">$5/mo</span>
                  </div>
                  <div className="flex justify-between border-b border-green-200/50 pb-3">
                    <span className="text-green-800 font-medium">TXT2PAY (SMS)</span>
                    <span className="font-bold text-green-950">$10/mo <span className="text-green-700/60 text-sm font-normal">+ $0.25/tx</span></span>
                  </div>
                  <div className="flex justify-between pb-1">
                    <span className="text-green-800 font-medium">No Hardware Required</span>
                    <span className="font-bold text-green-600">Included</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Integration Bento Row */}
          <div ref={integrationsRef} className="opacity-0 bg-white rounded-[2.5rem] border border-gray-100 p-10 hover:shadow-xl transition-all duration-500 overflow-hidden relative">
             <div className="grid grid-cols-1 md:grid-cols-3 gap-10 items-center">
                <div className="md:col-span-2 space-y-6">
                  <span className="inline-block px-4 py-1.5 rounded-full bg-gray-100 text-gray-700 font-bold text-sm">
                    ECOSYSTEM INTEGRATIONS
                  </span>
                  <h2 className="text-4xl font-extrabold text-gray-900">Connect everything. Pay less.</h2>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 pt-4">
                    <div className="border border-gray-100 p-6 rounded-2xl bg-gray-50 hover:bg-white transition-colors">
                      <div className="w-12 h-12 bg-blue-100 text-blue-600 rounded-xl flex items-center justify-center mb-4">
                        <i className="ri-file-list-3-line text-2xl"></i>
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">Electronic Invoicing</h3>
                      <p className="text-gray-500 font-medium mb-4">Half the cost of standalone invoice software like Bill.com.</p>
                      <p className="font-extrabold text-2xl text-gray-900">$10<span className="text-base font-medium text-gray-500">/mo</span></p>
                    </div>

                    <div className="border border-gray-100 p-6 rounded-2xl bg-gray-50 hover:bg-white transition-colors">
                      <div className="w-12 h-12 bg-indigo-100 text-indigo-600 rounded-xl flex items-center justify-center mb-4">
                        <i className="ri-shopping-cart-line text-2xl"></i>
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">Shopify Connect</h3>
                      <p className="text-gray-500 font-medium mb-4">Bypass "Shopify Payments" restrictions entirely.</p>
                      <p className="font-extrabold text-2xl text-gray-900">$20<span className="text-base font-medium text-gray-500">/mo</span> <span className="text-sm font-bold text-indigo-600">+ 0.50%</span></p>
                    </div>
                  </div>
                </div>

                <div className="md:col-span-1 h-full">
                  <img src="/pricing/pricing_integrations_art_1776903268535.png" alt="Integrations" className="w-full h-full min-h-[300px] object-cover rounded-2xl shadow-sm border border-gray-50" />
                </div>
             </div>
          </div>

        </div>

          {/* FAQ Section */}
          <div className="mt-20 max-w-3xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-12">
              <span className="inline-block px-4 py-1.5 rounded-full bg-blue-50 text-blue-700 font-bold text-sm mb-4 border border-blue-100">FAQ</span>
              <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-4">Common Questions</h2>
            </div>
            <div className="space-y-4">
              {[
                { q: "What PCI compliance level does Paygent maintain?", a: "Paygent is PCI DSS Level 1 certified, the highest level of compliance in the payments industry. When you use our tokenization APIs (Collect.js), your integration qualifies for SAQ-A, the simplest PCI self-assessment questionnaire." },
                { q: "Do you support ACH / eCheck payments?", a: "Yes. ACH payment processing is available as a $25/month add-on. This includes both ACH debits (pulling funds from a customer's bank account) and ACH credits (pushing funds for payouts)." },
                { q: "How fast are payouts / settlements?", a: "Standard settlement is next-business-day (T+1). Same-day settlement is available for qualifying merchants. Tapn transactions settle within 48 hours." },
                { q: "Can I migrate from Stripe or Square?", a: "Yes. We offer a white-glove migration service for merchants processing over $50K/month. We handle token migration, webhook re-mapping, and parallel testing." },
                { q: "Are there long-term contracts?", a: "No. Both Paygent Platform and Tapn are month-to-month with no cancellation fees. Enterprise custom pricing may include annual commitments with volume discounts." },
                { q: "What payment methods do you support?", a: "Visa, Mastercard, American Express, Discover, JCB, UnionPay, Apple Pay, Google Pay, ACH/eCheck, and contactless NFC (via Tapn). Local payment methods are available in select regions." },
                { q: "Do you offer chargeback protection?", a: "Paygent Radar provides AI-powered fraud scoring on every transaction. Combined with 3D Secure authentication and customizable risk rules, most fraud is blocked before it becomes a chargeback." },
                { q: "Is there a free trial?", a: "We offer a sandbox environment with full API access at no cost. You can test transactions, webhooks, and integrations before going live. No credit card required to start." },
              ].map((faq, i) => (
                <details key={i} className="group bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm">
                  <summary className="flex items-center justify-between p-6 cursor-pointer list-none">
                    <span className="font-bold text-gray-900 pr-4">{faq.q}</span>
                    <i className="ri-add-line text-gray-400 group-open:hidden w-5 h-5 flex items-center justify-center flex-shrink-0" />
                    <i className="ri-subtract-line text-blue-600 hidden group-open:flex w-5 h-5 items-center justify-center flex-shrink-0" />
                  </summary>
                  <div className="px-6 pb-6 text-gray-600 leading-relaxed text-sm -mt-2">{faq.a}</div>
                </details>
              ))}
            </div>
          </div>

      </main>
      
      <Footer />
    </div>
  );
}
